const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';
const API_URL = `${backendUrl}/api`;

export const getCustomers = async (all = false) => {
  const response = await fetch(`${API_URL}/customers${all ? '?all=true' : ''}`);
  if (!response.ok) {
    throw new Error('A resposta da rede não foi boa');
  }
  return response.json();
};

export const getCustomerMessages = async (customerId) => {
  const response = await fetch(`${API_URL}/customers/${customerId}/messages`);
  if (!response.ok) {
    throw new Error('A resposta da rede não foi boa');
  }
  return response.json();
};

export const getOrders = async (filters = {}) => {
  // Constrói a string de query a partir do objeto de filtros. Ex: { date: '2023-10-27' } vira "?date=2023-10-27"
  const queryParams = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([_, v]) => v != null && v !== ''))).toString();
  const url = `${API_URL}/orders${queryParams ? `?${queryParams}` : ''}`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('A resposta da rede não foi boa');
  }
  return response.json();
};

/**
 * Busca a lista de todos os produtos.
 * @returns {Promise<object>} Uma promessa que resolve para o objeto de produtos.
 */
export const getProducts = async () => {
  const response = await fetch(`${API_URL}/products`);
  if (!response.ok) {
    throw new Error('A resposta da rede não foi boa');
  }
  return response.json();
};

/**
 * Atualiza o status de um pedido.
 * @param {string} orderId - O ID do pedido.
 * @param {string} status - O novo status ('completed', 'canceled', etc.).
 * @returns {Promise<object>} O pedido atualizado.
 */
export const updateOrderStatus = async (orderId, status) => {
  const response = await fetch(`${API_URL}/orders/${orderId}/status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ status }),
  });
  if (!response.ok) {
    throw new Error('Falha ao atualizar o status do pedido');
  }
  return response.json();
};

export const sendMessageToCustomer = async (customerId, message) => {
  const response = await fetch(`${API_URL}/customers/${customerId}/send-message`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ message }),
  });
  if (!response.ok) {
    throw new Error('Falha ao enviar mensagem');
  }
  return response.json();
};

export const setCustomerStatus = async (customerId, status) => {
  const response = await fetch(`${API_URL}/customers/${customerId}/set-status`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ status }),
  });
  if (!response.ok) {
    throw new Error('Falha ao atualizar status do cliente');
  }
  return response.json();
};

export const sendMenuToCustomer = async (customerId, menuType) => {
  const response = await fetch(`${API_URL}/customers/${customerId}/send-menu`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ menuType }),
  });
  if (!response.ok) {
    throw new Error('Falha ao enviar menu');
  }
  return response.json();
};

/**
 * Envia os dados de uma nova venda manual para o backend.
 * @param {object} orderData - Os dados do pedido (cliente, carrinho, etc.).
 * @returns {Promise<object>} O novo pedido criado.
 */
export const createManualOrder = async (orderData) => {
  const response = await fetch(`${API_URL}/orders`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(orderData),
  });
  if (!response.ok) {
    throw new Error('Falha ao registrar a venda manual');
  }
  return response.json();
};

/**
 * Atualiza um pedido existente.
 * @param {string} orderId - O ID do pedido.
 * @param {object} orderData - Os novos dados do pedido.
 * @returns {Promise<object>} O pedido atualizado.
 */
export const updateOrder = async (orderId, orderData) => {
  const response = await fetch(`${API_URL}/orders/${orderId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(orderData),
  });
  if (!response.ok) {
    throw new Error('Falha ao atualizar o pedido');
  }
  return response.json();
};

/**
 * Envia um carrinho montado pelo admin para o cliente.
 * @param {string} customerId - O ID do cliente.
 * @param {Array} cart - O array de itens do carrinho.
 */
export const sendCartToCustomer = async (customerId, cart) => {
  const response = await fetch(`${API_URL}/customers/${customerId}/cart`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cart }),
  });
  if (!response.ok) {
    throw new Error('Falha ao enviar carrinho');
  }
  return response.json();
};

/**
 * Atualiza o estoque de um produto específico.
 * @param {string} productName - O nome do produto a ser atualizado.
 * @param {number} newStock - O novo valor do estoque.
 * @returns {Promise<object>} A resposta do servidor.
 */
export const updateProductStock = async (productName, newStock, variationName = null) => {
  // Usamos encodeURIComponent para garantir que nomes com espaços ou caracteres especiais funcionem na URL
  const response = await fetch(`${API_URL}/products/${encodeURIComponent(productName)}/stock`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ stock: newStock, variationName }), // Envia variationName se existir
  });
  if (!response.ok) {
    throw new Error('Falha ao atualizar o estoque do produto');
  }
  return response.json();
};

/**
 * Busca as configurações da aplicação.
 * @param {string} type - O tipo de configuração (ex: 'delivery').
 * @returns {Promise<object>}
 */
export const getSettings = async (type) => {
  const response = await fetch(`${API_URL}/settings?type=${type}`);
  if (!response.ok) throw new Error('Falha ao buscar configurações');
  return response.json();
};

/**
 * Atualiza as configurações.
 * @param {string} type - O tipo de configuração.
 * @param {object} data - Os dados para atualizar.
 * @returns {Promise<object>}
 */
export const updateSettings = async (type, data) => {
  const response = await fetch(`${API_URL}/settings`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ type, data }),
  });
  if (!response.ok) throw new Error('Falha ao atualizar configurações');
  return response.json();
};

/**
 * Adiciona um novo produto.
 * @param {object} productData - Dados do novo produto { name, price, stock, category }.
 * @returns {Promise<object>} A resposta do servidor.
 */
export const addProduct = async (productData) => {
  const response = await fetch(`${API_URL}/products`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(productData),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Falha ao adicionar o produto');
  }
  return response.json();
};

/**
 * Atualiza um produto existente.
 * @param {string} originalName - O nome original do produto.
 * @param {object} productData - Os novos dados do produto { newName, price, stock, category }.
 * @returns {Promise<object>} A resposta do servidor.
 */
export const updateProduct = async (originalName, productData) => {
  const response = await fetch(`${API_URL}/products/${encodeURIComponent(originalName)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(productData),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Falha ao atualizar o produto');
  }
  return response.json();
};

/**
 * Deleta um produto.
 * @param {string} productName - O nome do produto a ser deletado.
 * @returns {Promise<object>} A resposta do servidor.
 */
export const deleteProduct = async (productName) => {
  const response = await fetch(`${API_URL}/products/${encodeURIComponent(productName)}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error('Falha ao deletar o produto');
  }
  return response.json();
};

/**
 * Confirma o pagamento de um pedido.
 * @param {string} orderId - O ID do pedido.
 */
export const confirmPayment = async (orderId) => {
  console.log(`[apiServices] confirmPayment - Enviando requisição para ${API_URL}/orders/${orderId}/confirm-payment`);
  const response = await fetch(`${API_URL}/orders/${orderId}/confirm-payment`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  console.log(`[apiServices] confirmPayment - Resposta recebida. Status: ${response.status}, OK: ${response.ok}`);

  if (!response.ok) {
    // Tenta extrair uma mensagem de erro do corpo da resposta, se houver.
    const errorData = await response.json().catch(() => ({ message: 'Falha ao confirmar o pagamento' }));
    throw new Error(errorData.message || 'Falha ao confirmar o pagamento');
  }
  
  try {
    return await response.json();
  } catch (jsonError) {
    // Se a resposta for OK (2xx) mas o corpo não for um JSON válido (ou estiver vazio), retorna um objeto de sucesso.
    return { success: true, message: 'Operação concluída com corpo de resposta vazio.' };
  }
};

/**
 * Deleta um motoboy.
 * @param {string} motoboyId - O ID do motoboy a ser deletado.
 */
export const deleteMotoboy = async (motoboyId) => {
  const response = await fetch(`${API_URL}/motoboys/${motoboyId}`, {
    method: 'DELETE',
  });
  if (!response.ok) {
    throw new Error('Falha ao deletar motoboy');
  }
  return response.json();
};

/**
 * Busca a lista de motoboys.
 */
export const getMotoboys = async () => {
  const response = await fetch(`${API_URL}/motoboys`);
  if (!response.ok) {
    throw new Error('Falha ao buscar motoboys');
  }
  return response.json();
};

/**
 * Adiciona um novo motoboy.
 * @param {string} name - Nome do motoboy.
 */
export const addMotoboy = async (name) => {
  const response = await fetch(`${API_URL}/motoboys`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name }),
  });
  if (!response.ok) {
    throw new Error('Falha ao adicionar motoboy');
  }
  return response.json();
};

/**
 * Envia a senha para o endpoint de login.
 * @param {string} password - A senha a ser validada.
 * @param {string} totpCode - O código 2FA do Google Authenticator.
 * @param {string} setupSecret - O segredo temporário para configurar o 2FA.
 * @returns {Promise<object>} A resposta do servidor.
 */
export const login = async ({ username, password, totpCode, setupSecret }) => {
  const response = await fetch(`${API_URL}/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password, totpCode, setupSecret }),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.message || 'Falha na autenticação');
  }
  return response.json();
};

/**
 * Busca os dados da análise do dia anterior.
 * @param {object} filters - Objeto com filtros, ex: { startDate, endDate }.
 * @returns {Promise<object>} Uma promessa que resolve para o objeto de análise.
 */
export const getAnalysisData = async (filters = {}) => {
  const queryParams = new URLSearchParams();
  if (filters.startDate) queryParams.append('startDate', filters.startDate);
  if (filters.endDate) queryParams.append('endDate', filters.endDate);

  const queryString = queryParams.toString();
  const url = `${API_URL}/analysis${queryString ? `?${queryString}` : ''}`;

  const response = await fetch(url);
  if (!response.ok) {
    throw new Error('Falha ao buscar dados da análise');
  }
  return response.json();
};

/**
 * Atualiza os dados de um cliente.
 * @param {string} customerId - O ID do cliente.
 * @param {object} customerData - Os novos dados do cliente.
 * @returns {Promise<object>} A resposta do servidor.
 */
export const updateCustomer = async (customerId, customerData) => {
  const response = await fetch(`${API_URL}/customers/${customerId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(customerData),
  });
  if (!response.ok) {
    throw new Error('Falha ao atualizar o cliente');
  }
  return response.json();
};

/**
 * Busca o modo de pedido atual do servidor (vendas do dia ou apenas encomendas).
 * @returns {Promise<object>} Uma promessa que resolve para { mode: 'daily' | 'pre-order' }.
 */
export const getOrderingMode = async () => {
  const response = await fetch(`${API_URL}/ordering-mode`);
  if (!response.ok) {
    throw new Error('Falha ao buscar o modo de pedido');
  }
  return response.json();
};

/**
 * Define o modo de pedido no servidor.
 * @param {'daily' | 'pre-order'} mode - O novo modo de pedido.
 * @returns {Promise<object>} A resposta do servidor.
 */
export const setOrderingMode = async (mode) => {
  const response = await fetch(`${API_URL}/ordering-mode`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mode }),
  });
  if (!response.ok) {
    throw new Error('Falha ao definir o modo de pedido');
  }
  return response.json();
};
